import React, { useState } from 'react';
import { TravelMode } from '../types';

export interface TripConfig {
  travelMode: TravelMode;
  radius: number; // in km
  maxTime: number; // in minutes
  numberOfPeople: number;
}

interface TripConfigurationProps {
  onComplete: (config: TripConfig) => void;
  onBack: () => void;
  initialConfig?: TripConfig;
}

const TRAVEL_MODES = [
  { value: 'DRIVING' as TravelMode, label: 'Car', icon: 'directions_car' },
  { value: 'BICYCLING' as TravelMode, label: 'Bike', icon: 'directions_bike' },
  { value: 'WALKING' as TravelMode, label: 'Walk', icon: 'directions_walk' },
  { value: 'TRANSIT' as TravelMode, label: 'Public Transport', icon: 'directions_transit' },
];

// Slider configuration
const RADIUS_SLIDER = {
  min: 1,
  max: 50,
  step: 1,
  marks: [1, 5, 10, 20, 30, 50],
};

const TIME_SLIDER = {
  min: 15,
  max: 480,
  step: 15,
  marks: [60, 120, 240, 480],
};

// Helper to format time for display
const formatTime = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  return `${hours}h ${mins}m`;
};

const PEOPLE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const TripConfiguration: React.FC<TripConfigurationProps> = ({ onComplete, onBack, initialConfig }) => {
  const [travelMode, setTravelMode] = useState<TravelMode>(initialConfig?.travelMode || 'DRIVING');
  const [numberOfPeople, setNumberOfPeople] = useState<number>(initialConfig?.numberOfPeople ?? 1);
  const [radius, setRadius] = useState<number>(initialConfig?.radius || 5); // default 5 km
  const [maxTime, setMaxTime] = useState<number>(initialConfig?.maxTime || 120); // default 2 hours
  const [customRadius, setCustomRadius] = useState<string>('');
  const [customMaxTime, setCustomMaxTime] = useState<string>('');
  const [showCustomRadius, setShowCustomRadius] = useState(false);
  const [showCustomMaxTime, setShowCustomMaxTime] = useState(false);

  const handleContinue = () => {
    const finalRadius = showCustomRadius && customRadius ? parseFloat(customRadius) : radius;
    const finalMaxTime = showCustomMaxTime && customMaxTime ? parseInt(customMaxTime) : maxTime;

    if (isNaN(finalRadius) || finalRadius <= 0) {
      alert('Please enter a valid radius in km');
      return;
    }

    if (isNaN(finalMaxTime) || finalMaxTime <= 0) {
      alert('Please enter a valid maximum travel time in minutes');
      return;
    }

    onComplete({
      travelMode,
      radius: finalRadius,
      maxTime: finalMaxTime,
      numberOfPeople,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col p-4">
      {/* Header */}
      <div className="mb-6 mt-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors mb-4"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Configure Your Trip</h1>
        <p className="text-lg text-gray-600">Choose your travel preferences</p>
      </div>

      <div className="flex-1 overflow-y-auto pb-4">
        <div className="max-w-md mx-auto w-full space-y-6">
          {/* Travel Mode Selection */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Travel Mode</h2>
            <div className="grid grid-cols-2 gap-3">
              {TRAVEL_MODES.map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => setTravelMode(mode.value)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    travelMode === mode.value
                      ? 'border-blue-600 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <span
                      className={`material-symbols-outlined text-3xl ${
                        travelMode === mode.value ? 'text-blue-600' : 'text-gray-600'
                      }`}
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      {mode.icon}
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        travelMode === mode.value ? 'text-blue-900' : 'text-gray-700'
                      }`}
                    >
                      {mode.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* How many travelers */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">How many travelers?</h2>
            <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
              {PEOPLE_OPTIONS.map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setNumberOfPeople(num)}
                  className={`flex-shrink-0 w-10 h-10 rounded-xl font-semibold transition-all ${
                    numberOfPeople === num
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-300'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          {/* Radius Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Search Radius</h2>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${showCustomRadius ? 'text-gray-400' : 'text-blue-600'}`}>
                  {showCustomRadius ? (customRadius || '—') : radius}
                </span>
                <span className="text-gray-500 font-medium">km</span>
              </div>
            </div>

            {/* Slider Container */}
            <div className={`bg-white rounded-2xl p-5 border border-gray-100 shadow-sm transition-all ${showCustomRadius ? 'opacity-50' : ''}`}>
              {/* Slider Track */}
              <div className="relative pt-2 pb-6">
                <input
                  type="range"
                  min={RADIUS_SLIDER.min}
                  max={RADIUS_SLIDER.max}
                  step={RADIUS_SLIDER.step}
                  value={showCustomRadius ? RADIUS_SLIDER.min : radius}
                  onChange={(e) => {
                    if (!showCustomRadius) {
                      setRadius(parseInt(e.target.value));
                    }
                  }}
                  disabled={showCustomRadius}
                  className="w-full h-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full appearance-none cursor-pointer disabled:cursor-not-allowed
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-6
                    [&::-webkit-slider-thumb]:h-6
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-gradient-to-br
                    [&::-webkit-slider-thumb]:from-blue-500
                    [&::-webkit-slider-thumb]:to-indigo-600
                    [&::-webkit-slider-thumb]:shadow-lg
                    [&::-webkit-slider-thumb]:shadow-blue-500/30
                    [&::-webkit-slider-thumb]:border-4
                    [&::-webkit-slider-thumb]:border-white
                    [&::-webkit-slider-thumb]:transition-transform
                    [&::-webkit-slider-thumb]:hover:scale-110
                    [&::-webkit-slider-thumb]:active:scale-95
                    [&::-moz-range-thumb]:w-6
                    [&::-moz-range-thumb]:h-6
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-gradient-to-br
                    [&::-moz-range-thumb]:from-blue-500
                    [&::-moz-range-thumb]:to-indigo-600
                    [&::-moz-range-thumb]:shadow-lg
                    [&::-moz-range-thumb]:border-4
                    [&::-moz-range-thumb]:border-white
                    [&::-moz-range-thumb]:cursor-pointer"
                  style={{
                    background: showCustomRadius
                      ? 'linear-gradient(to right, #e5e7eb, #e5e7eb)'
                      : `linear-gradient(to right, #3b82f6 0%, #6366f1 ${((radius - RADIUS_SLIDER.min) / (RADIUS_SLIDER.max - RADIUS_SLIDER.min)) * 100}%, #e0e7ff ${((radius - RADIUS_SLIDER.min) / (RADIUS_SLIDER.max - RADIUS_SLIDER.min)) * 100}%, #e0e7ff 100%)`
                  }}
                />

                {/* Marks */}
                <div className="relative mt-3 px-1">
                  {RADIUS_SLIDER.marks.map((mark) => {
                    const position = ((mark - RADIUS_SLIDER.min) / (RADIUS_SLIDER.max - RADIUS_SLIDER.min)) * 100;
                    return (
                      <button
                        key={mark}
                        onClick={() => {
                          if (!showCustomRadius) {
                            setRadius(mark);
                          }
                        }}
                        disabled={showCustomRadius}
                        className={`absolute text-xs font-medium transition-colors transform -translate-x-1/2 ${
                          radius === mark && !showCustomRadius
                            ? 'text-blue-600'
                            : 'text-gray-400 hover:text-gray-600'
                        } disabled:hover:text-gray-400`}
                        style={{ left: `${position}%` }}
                      >
                        {mark}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Custom Toggle */}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={() => {
                  setShowCustomRadius(!showCustomRadius);
                  if (!showCustomRadius) {
                    setCustomRadius('');
                  }
                }}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  showCustomRadius ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                    showCustomRadius ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm font-medium text-gray-700">Custom value</span>
            </div>

            {/* Custom Input */}
            {showCustomRadius && (
              <div className="mt-3 animate-in slide-in-from-top-2 duration-200">
                <div className="relative">
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={customRadius}
                    onChange={(e) => setCustomRadius(e.target.value)}
                    placeholder="Enter radius (e.g., 75)"
                    className="w-full bg-white rounded-xl py-3.5 pl-4 pr-14 text-gray-900 placeholder-gray-400 border-2 border-blue-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-lg font-medium"
                    autoFocus
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-600 font-semibold">km</span>
                </div>
                <p className="text-xs text-gray-500 mt-2 ml-1">
                  Use custom for distances greater than {RADIUS_SLIDER.max} km
                </p>
              </div>
            )}
          </div>

          {/* Maximum Travel Time Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Maximum Travel Time</h2>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${showCustomMaxTime ? 'text-gray-400' : 'text-indigo-600'}`}>
                  {showCustomMaxTime ? (customMaxTime ? formatTime(parseInt(customMaxTime)) : '—') : formatTime(maxTime)}
                </span>
              </div>
            </div>

            {/* Slider Container */}
            <div className={`bg-white rounded-2xl p-5 border border-gray-100 shadow-sm transition-all ${showCustomMaxTime ? 'opacity-50' : ''}`}>
              {/* Slider Track */}
              <div className="relative pt-2 pb-6">
                <input
                  type="range"
                  min={TIME_SLIDER.min}
                  max={TIME_SLIDER.max}
                  step={TIME_SLIDER.step}
                  value={showCustomMaxTime ? TIME_SLIDER.min : maxTime}
                  onChange={(e) => {
                    if (!showCustomMaxTime) {
                      setMaxTime(parseInt(e.target.value));
                    }
                  }}
                  disabled={showCustomMaxTime}
                  className="w-full h-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full appearance-none cursor-pointer disabled:cursor-not-allowed
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-6
                    [&::-webkit-slider-thumb]:h-6
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-gradient-to-br
                    [&::-webkit-slider-thumb]:from-indigo-500
                    [&::-webkit-slider-thumb]:to-purple-600
                    [&::-webkit-slider-thumb]:shadow-lg
                    [&::-webkit-slider-thumb]:shadow-indigo-500/30
                    [&::-webkit-slider-thumb]:border-4
                    [&::-webkit-slider-thumb]:border-white
                    [&::-webkit-slider-thumb]:transition-transform
                    [&::-webkit-slider-thumb]:hover:scale-110
                    [&::-webkit-slider-thumb]:active:scale-95
                    [&::-moz-range-thumb]:w-6
                    [&::-moz-range-thumb]:h-6
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-gradient-to-br
                    [&::-moz-range-thumb]:from-indigo-500
                    [&::-moz-range-thumb]:to-purple-600
                    [&::-moz-range-thumb]:shadow-lg
                    [&::-moz-range-thumb]:border-4
                    [&::-moz-range-thumb]:border-white
                    [&::-moz-range-thumb]:cursor-pointer"
                  style={{
                    background: showCustomMaxTime
                      ? 'linear-gradient(to right, #e5e7eb, #e5e7eb)'
                      : `linear-gradient(to right, #6366f1 0%, #9333ea ${((maxTime - TIME_SLIDER.min) / (TIME_SLIDER.max - TIME_SLIDER.min)) * 100}%, #e0e7ff ${((maxTime - TIME_SLIDER.min) / (TIME_SLIDER.max - TIME_SLIDER.min)) * 100}%, #e0e7ff 100%)`
                  }}
                />

                {/* Marks */}
                <div className="relative mt-3 px-1">
                  {TIME_SLIDER.marks.map((mark) => {
                    const position = ((mark - TIME_SLIDER.min) / (TIME_SLIDER.max - TIME_SLIDER.min)) * 100;
                    return (
                      <button
                        key={mark}
                        onClick={() => {
                          if (!showCustomMaxTime) {
                            setMaxTime(mark);
                          }
                        }}
                        disabled={showCustomMaxTime}
                        className={`absolute text-xs font-medium transition-colors transform -translate-x-1/2 ${
                          maxTime === mark && !showCustomMaxTime
                            ? 'text-indigo-600'
                            : 'text-gray-400 hover:text-gray-600'
                        } disabled:hover:text-gray-400`}
                        style={{ left: `${position}%` }}
                      >
                        {formatTime(mark)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Custom Toggle */}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={() => {
                  setShowCustomMaxTime(!showCustomMaxTime);
                  if (!showCustomMaxTime) {
                    setCustomMaxTime('');
                  }
                }}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  showCustomMaxTime ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                    showCustomMaxTime ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm font-medium text-gray-700">Custom value</span>
            </div>

            {/* Custom Input */}
            {showCustomMaxTime && (
              <div className="mt-3 animate-in slide-in-from-top-2 duration-200">
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={customMaxTime}
                    onChange={(e) => setCustomMaxTime(e.target.value)}
                    placeholder="Enter time (e.g., 600)"
                    className="w-full bg-white rounded-xl py-3.5 pl-4 pr-14 text-gray-900 placeholder-gray-400 border-2 border-indigo-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-lg font-medium"
                    autoFocus
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-600 font-semibold">min</span>
                </div>
                <p className="text-xs text-gray-500 mt-2 ml-1">
                  Use custom for durations greater than {formatTime(TIME_SLIDER.max)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <div className="pt-4 pb-safe-bottom">
        <button
          onClick={handleContinue}
          className="w-full max-w-md mx-auto bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl py-4 font-semibold text-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
        >
          <span>Continue</span>
          <span className="material-symbols-outlined">arrow_forward</span>
        </button>
      </div>
    </div>
  );
};
